export type FrameBuilderPlatePlacement = {
    left: number;
    top: number;
    scale: number;
};

export type FrameBuilderPlatePlacementInput = {
    bevelLeft: string | undefined;
    bevelTop: string | undefined;
    bevelWidth: string | undefined;
    bevelHeight: string | undefined;
    matWidth: string | undefined;
    matHeight: string | undefined;
    mouldingInches: number;
    contentWidthInches: number;
    contentHeightInches: number;
    dpi: number;
    // FrameBuilder's bottom rail. A plate that lands on the document is moved into this one.
    railBottomInches?: number;
    // The rail was lengthened for this plate. Center it so the mat above and below matches the product renderer.
    centerInBottomRail?: boolean;
};

const readPx = (value: string | undefined): number | null => {
    if (value == null || value === '') {
        return null;
    }

    const parsed = parseFloat(value);

    return Number.isFinite(parsed) ? parsed : null;
};

const roundPx = (value: number): number => Math.round(value * 1000) / 1000;

const roundScale = (value: number): number => Math.round(value * 10000) / 10000;

// FrameBuilder's mat is a different size from the product renderer's (its rails are fixed).
// 1 keeps the plate at the measured pixel offset inside that mat.
const axisScale = (matSize: string | undefined, contentInches: number, dpi: number): number => {
    const oldMat = readPx(matSize);
    const nextMat = contentInches > 0 && dpi > 0 ? contentInches * dpi : 0;

    if (oldMat == null || oldMat <= 0 || nextMat <= 0) {
        return 1;
    }

    return nextMat / oldMat;
};

/**
 * ProductRender positions the plate inside the primary mat. FrameBuilder's origin is the
 * outside of the moulding. The plate keeps its measured box, scaled uniformly on the mat's
 * width so the type is not stretched, and its center stays at the same fraction of the mat.
 */
export const placeFrameBuilderPlate = (input: FrameBuilderPlatePlacementInput): FrameBuilderPlatePlacement | null => {
    const bevelLeft = readPx(input.bevelLeft);
    const bevelTop = readPx(input.bevelTop);
    const bevelWidth = readPx(input.bevelWidth);
    const bevelHeight = readPx(input.bevelHeight);

    if (bevelLeft == null || bevelTop == null || bevelWidth == null || bevelHeight == null || bevelWidth <= 0 || bevelHeight <= 0) {
        return null;
    }

    const dpi = input.dpi > 0 ? input.dpi : 0;
    const mouldingPx = input.mouldingInches > 0 ? input.mouldingInches * dpi : 0;
    const scale = roundScale(axisScale(input.matWidth, input.contentWidthInches, dpi));
    const scaleY = roundScale(axisScale(input.matHeight, input.contentHeightInches, dpi));
    const centerX = mouldingPx + (bevelLeft + bevelWidth / 2) * scale;
    const centerY = mouldingPx + (bevelTop + bevelHeight / 2) * scaleY;
    const left = centerX - (bevelWidth * scale) / 2;
    const top = centerY - (bevelHeight * scale) / 2;

    if (!Number.isFinite(scale) || scale <= 0 || !Number.isFinite(left) || !Number.isFinite(top)) {
        return null;
    }

    return fitPlateInBottomRail(
        {
            left: roundPx(left),
            top: roundPx(top),
            scale,
        },
        bevelWidth,
        bevelHeight,
        mouldingPx,
        input.contentHeightInches > 0 && dpi > 0 ? input.contentHeightInches * dpi : 0,
        input.railBottomInches != null && input.railBottomInches > 0 && dpi > 0 ? input.railBottomInches * dpi : 0,
        !!input.centerInBottomRail,
    );
};

// A plate already inside the bottom rail stays put, unless that rail was lengthened for it:
// then it is centered, which is the gap the product renderer leaves above and below.
// One that crosses onto the document is centered in the rail FrameBuilder actually has,
// and scaled down only when it is taller than that rail. A document-sized plate is left
// on its measured box.
const fitPlateInBottomRail = (placement: FrameBuilderPlatePlacement, bevelWidth: number, bevelHeight: number, mouldingPx: number, matHeightPx: number, railHeightPx: number, centerInBottomRail: boolean): FrameBuilderPlatePlacement => {
    if (!(matHeightPx > 0) || !(railHeightPx > 0) || railHeightPx >= matHeightPx) {
        return placement;
    }

    const visualH = bevelHeight * placement.scale;
    const visualW = bevelWidth * placement.scale;

    if (!(visualH > 0) || !(visualW > 0) || visualH >= matHeightPx * 0.5) {
        return placement;
    }

    const matBottom = mouldingPx + matHeightPx;
    const railStart = matBottom - railHeightPx;
    const slack = 1;

    if (!centerInBottomRail && placement.top >= railStart - slack && placement.top + visualH <= matBottom + slack) {
        return placement;
    }

    let scale = placement.scale;
    let nextH = visualH;
    let nextW = visualW;

    if (nextH > railHeightPx) {
        scale = roundScale(placement.scale * (railHeightPx / nextH));
        if (!(scale > 0)) {
            return placement;
        }
        nextH = bevelHeight * scale;
        nextW = bevelWidth * scale;
    }

    return {
        left: roundPx(placement.left + visualW / 2 - nextW / 2),
        top: roundPx(railStart + (railHeightPx - nextH) / 2),
        scale,
    };
};
